import * as React from 'react'
import { Link, Navigate } from 'react-router-dom'
import {
  BookOpen,
  CalendarDays,
  Clock,
  ExternalLink,
  GraduationCap,
  MapPin,
  MessageCircle,
  Presentation,
  RotateCcw,
  Sparkles,
  Video,
} from 'lucide-react'

import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { featureOn } from '@/lib/features'
import { dayLabel, entriesToday, hasToday, localDayIso, meetingsToday, startOfLocalDay, timeRange } from '@/lib/today'
import { sortLinks } from '@/lib/schedule-links'
import type {
  Activity,
  ScheduleEntry,
  ScheduleEntryKind,
  ScheduleEntryLinkWithTarget,
  Workshop,
  WorkshopTopic,
} from '@/lib/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ErrorNote, Loading, Markdown, Page } from '@/components/page'
import { EntryLinks } from '@/components/entry-links'

/** Matches the Schedule's own icons, so a slot looks like itself on both pages. */
const KIND_ICON: Record<ScheduleEntryKind, React.ComponentType<{ className?: string }>> = {
  topic: Presentation,
  workshop: GraduationCap,
  reading: BookOpen,
  training: Sparkles,
  discussion: MessageCircle,
  retro: RotateCcw,
  other: CalendarDays,
}

/** As far back as a still-running meeting can have started. Mirrors use-today. */
const MULTI_DAY_LOOKBACK_DAYS = 7

/**
 * Today — the day-of page.
 *
 * It exists only on a day the team is meeting or has something on the plan
 * (`hasToday`), and it disappears again tomorrow. That is the whole idea: a
 * page you open at the start of a session that holds the meeting, the plan for
 * it, and — the reason it was asked for — one click to each board, evaluation
 * and game the lead said you would need, instead of eight people watching
 * someone search Boards for "the retro one".
 *
 * NOTHING HERE IS NEW DATA. Every row on this page belongs to Workshops,
 * Schedule, Boards, Evaluations or Games, and all of it is readable there. This
 * is a view of one day, so it writes nothing at all — the status select, the
 * note editor and the link editor stay on the Schedule, where the plan is
 * actually made. A page people open mid-meeting is the wrong place to be able
 * to change the plan by accident.
 *
 * When the day is empty the page REDIRECTS rather than drawing an empty state.
 * It was asked for as a page that only exists when there is something on, and
 * the nav entry (useTodayGate) appears on the same condition — so an empty
 * Today is only ever reached by a stale link or by the day turning over while
 * the tab was open, and both of those want the dashboard.
 */
export function TodayPage() {
  const { currentTeam } = useAuth()
  const teamId = currentTeam?.id
  const workshopsOn = featureOn(currentTeam, 'workshops_enabled')
  const scheduleOn = featureOn(currentTeam, 'schedule_enabled')
  // One `now` for the whole render, so the day cannot change between the filter
  // and the heading.
  const now = React.useMemo(() => new Date(), [])

  const { data, loading, error } = useQuery(async () => {
    const empty = {
      meetings: [] as Workshop[],
      lessons: [] as WorkshopTopic[],
      entries: [] as ScheduleEntry[],
      links: [] as ScheduleEntryLinkWithTarget[],
      topics: [] as Activity[],
    }
    if (!teamId) return empty

    const since = startOfLocalDay(now)
    since.setDate(since.getDate() - MULTI_DAY_LOOKBACK_DAYS)

    const [workshops, entries] = await Promise.all([
      workshopsOn
        ? sb().from('workshops').select('*').eq('team_id', teamId).gte('starts_at', since.toISOString())
        : Promise.resolve({ data: [], error: null }),
      scheduleOn
        ? sb().from('schedule_entries').select('*').eq('team_id', teamId).eq('scheduled_on', localDayIso(now))
        : Promise.resolve({ data: [], error: null }),
    ])
    if (workshops.error) throw workshops.error
    if (entries.error) throw entries.error

    const meetings = meetingsToday((workshops.data ?? []) as Workshop[], now)
    const todays = entriesToday((entries.data ?? []) as ScheduleEntry[], now)
    if (!hasToday(meetings, todays)) return empty

    // Dependent reads, so they are asked for only what today actually needs —
    // and only when there is a today at all.
    const entryIds = todays.map((e) => e.id)
    const [lessons, links] = await Promise.all([
      meetings.length
        ? sb()
            .from('workshop_topics')
            .select('*')
            .in('workshop_id', meetings.map((m) => m.id))
            .order('display_order')
        : Promise.resolve({ data: [], error: null }),
      entryIds.length
        ? sb()
            .from('schedule_entry_links')
            // The embeds are what make a link renderable: there is no stored
            // copy of a target's name (0064), so without these every pill would
            // read "Untitled".
            .select(
              '*, boards(id, title, archived), evaluations(id, name, status, conducted_on), games(id, slug, title, min_players, active)',
            )
            .in('entry_id', entryIds)
            .order('display_order')
            .order('created_at')
        : Promise.resolve({ data: [], error: null }),
    ])
    if (lessons.error) throw lessons.error
    if (links.error) throw links.error

    // The live title for a slot that names a topic, and for a workshop's
    // lessons — same rule as the Schedule, which prefers the topic's current
    // title over the copy taken when the row was written.
    const wanted = [
      ...todays.map((e) => e.activity_id),
      ...((lessons.data ?? []) as WorkshopTopic[]).map((l) => l.activity_id),
    ].filter((id): id is string => Boolean(id))
    const topics = wanted.length
      ? await sb().from('activities').select('*').in('id', [...new Set(wanted)])
      : { data: [], error: null }
    if (topics.error) throw topics.error

    return {
      meetings,
      lessons: (lessons.data ?? []) as WorkshopTopic[],
      entries: todays,
      links: (links.data ?? []) as ScheduleEntryLinkWithTarget[],
      topics: (topics.data ?? []) as Activity[],
    }
  }, [teamId, workshopsOn, scheduleOn, now])

  const topicsById = React.useMemo(
    () => new Map((data?.topics ?? []).map((t) => [t.id, t])),
    [data?.topics],
  )

  const linksFor = React.useCallback(
    (entryId: string) => sortLinks((data?.links ?? []).filter((l) => l.entry_id === entryId)),
    [data?.links],
  )

  if (loading && !data) return <Loading label="Looking at today…" />
  if (error) {
    return (
      <Page title="Today" description={dayLabel(now)}>
        <ErrorNote error={error} />
      </Page>
    )
  }
  // The day emptied out, or someone followed a link to a page that was never
  // there. Nothing to say about it — go where the app goes by default.
  if (!data || !hasToday(data.meetings, data.entries)) return <Navigate to="/dashboard" replace />

  return (
    <Page
      title="Today"
      description={dayLabel(now)}
      actions={
        scheduleOn && (
          <Button asChild variant="outline" size="sm">
            <Link to="/schedule">
              <CalendarDays /> The whole plan
            </Link>
          </Button>
        )
      }
    >
      <div className="space-y-6">
        {data.meetings.length > 0 && (
          <section>
            <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">
              {data.meetings.length === 1 ? 'The meeting' : 'Meetings'}
            </h2>
            <div className="space-y-2.5">
              {data.meetings.map((m) => (
                <MeetingCard
                  key={m.id}
                  meeting={m}
                  lessons={data.lessons.filter((l) => l.workshop_id === m.id)}
                  topicTitle={(id) => topicsById.get(id)?.title}
                />
              ))}
            </div>
          </section>
        )}

        {data.entries.length > 0 && (
          <section>
            <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">On the plan</h2>
            <div className="space-y-2.5">
              {data.entries.map((e) => (
                <PlanCard
                  key={e.id}
                  entry={e}
                  topic={e.activity_id ? topicsById.get(e.activity_id) : undefined}
                  links={linksFor(e.id)}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </Page>
  )
}

function MeetingCard({
  meeting,
  lessons,
  topicTitle,
}: {
  meeting: Workshop
  lessons: WorkshopTopic[]
  /** The topic's live title, when the lesson still points at one. */
  topicTitle: (id: string) => string | undefined
}) {
  const when = timeRange(meeting.starts_at, meeting.ends_at)
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{meeting.title}</span>
              {meeting.status !== 'scheduled' && (
                <Badge variant="muted" className="text-[10px]">
                  {meeting.status}
                </Badge>
              )}
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {when && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {when}
                </span>
              )}
              {meeting.location && (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {meeting.location}
                </span>
              )}
              {meeting.facilitator && <span>Facilitated by {meeting.facilitator}</span>}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {/* The one thing somebody opening this page at 8:59 actually
                wants. External, so it opens beside the portal rather than
                replacing it. */}
            {meeting.meeting_url && (
              <Button asChild size="sm">
                <a href={meeting.meeting_url} target="_blank" rel="noreferrer">
                  <Video /> Join
                </a>
              </Button>
            )}
            <Button asChild variant="outline" size="sm">
              <Link to="/workshops">
                <ExternalLink /> Workshop
              </Link>
            </Button>
          </div>
        </div>

        {meeting.description && (
          <Markdown source={meeting.description} className="text-sm text-muted-foreground" />
        )}

        {lessons.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Covering</span>
            {lessons.map((l) => {
              const live = l.activity_id ? topicTitle(l.activity_id) : undefined
              return l.activity_id ? (
                <Button key={l.id} asChild variant="outline" size="sm">
                  <Link to={`/activities/${l.activity_id}`}>
                    <Presentation /> {live ?? l.title}
                  </Link>
                </Button>
              ) : (
                <Badge key={l.id} variant="muted" className="text-[10px]">
                  {l.title}
                </Badge>
              )
            })}
          </div>
        )}

        {/* The running order, if someone wrote one. It is the other half of
            "what are we doing for the next ninety minutes". */}
        {meeting.agenda && (
          <div className="rounded-md border bg-muted/30 px-3 py-2">
            <Markdown source={meeting.agenda} className="text-sm text-muted-foreground" />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function PlanCard({
  entry,
  topic,
  links,
}: {
  entry: ScheduleEntry
  topic?: Activity
  links: ScheduleEntryLinkWithTarget[]
}) {
  const Icon = KIND_ICON[entry.kind]
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <div className="mt-0.5 shrink-0 rounded-md bg-muted p-2 text-muted-foreground">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {topic ? (
              <Link to={`/activities/${topic.id}`} className="font-medium hover:underline">
                {topic.title}
              </Link>
            ) : (
              <span className="font-medium">{entry.title}</span>
            )}
            {entry.status === 'done' && (
              <Badge variant="success" className="text-[10px]">
                done
              </Badge>
            )}
            {entry.duration_minutes && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" /> {entry.duration_minutes} min
              </span>
            )}
          </div>

          {entry.notes && (
            <div className="rounded-md border bg-muted/30 px-3 py-2">
              <Markdown source={entry.notes} className="text-sm text-muted-foreground" />
            </div>
          )}

          {/* The point of the page. One row of buttons, in the order the lead
              put them in. */}
          <EntryLinks links={links} />
        </div>
      </CardContent>
    </Card>
  )
}
